import React from 'react';
import { Lock, Star, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * AccessGate Component
 * Shows upgrade prompts when guests try to access restricted features
 */
const AccessGate = ({ feature, children, showUpgrade = true }) => {
  const navigate = useNavigate();

  if (!showUpgrade) {
    return null;
  }

  const features = {
    community: {
      title: 'Community Access',
      description: 'Connect with fellow mentees, share insights, and grow together',
      icon: <Lock className="w-12 h-12 text-purple-500" />
    },
    uploads: {
      title: 'File Uploads',
      description: 'Submit assignments and documents to your mentor',
      icon: <Lock className="w-12 h-12 text-purple-500" />
    },
    dashboard_full: {
      title: 'Full Dashboard',
      description: 'Track your progress, points, level, and mentorship journey',
      icon: <Lock className="w-12 h-12 text-purple-500" />
    },
    unlimited_appointments: {
      title: 'Unlimited Q&A Sessions',
      description: 'Book as many mentorship sessions as you need',
      icon: <Lock className="w-12 h-12 text-purple-500" />
    },
    free_ebooks: {
      title: 'Free Ebooks',
      description: 'Access all 71 spiritual resources at no cost',
      icon: <Lock className="w-12 h-12 text-purple-500" />
    }
  };

  const selectedFeature = features[feature] || features.community;

  return (
    <div className="min-h-[400px] flex items-center justify-center p-4 sm:p-8">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 lg:p-12 text-center border-2 border-purple-100">
          <div className="flex justify-center mb-4 sm:mb-6">
            {selectedFeature.icon}
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4">
            {selectedFeature.title}
          </h2>
          
          <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
            {selectedFeature.description}
          </p>

          <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-center mb-4">
              <Star className="w-6 h-6 text-purple-600 mr-2" />
              <span className="text-xl font-semibold text-gray-900">Academy Membership Required</span>
            </div>
            
            <p className="text-gray-700 mb-6">
              Unlock this feature and everything else with Academy membership starting from <strong>£120/month</strong> or save with our annual plans
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 text-left text-sm">
              <div className="flex items-start">
                <div className="text-green-500 mr-2">✓</div>
                <div>Full Community access</div>
              </div>
              <div className="flex items-start">
                <div className="text-green-500 mr-2">✓</div>
                <div>All 71 ebooks FREE</div>
              </div>
              <div className="flex items-start">
                <div className="text-green-500 mr-2">✓</div>
                <div>2 FREE courses</div>
              </div>
              <div className="flex items-start">
                <div className="text-green-500 mr-2">✓</div>
                <div>1 monthly Q&A session</div>
              </div>
              <div className="flex items-start">
                <div className="text-green-500 mr-2">✓</div>
                <div>Progress tracking</div>
              </div>
              <div className="flex items-start">
                <div className="text-green-500 mr-2">✓</div>
                <div>File uploads</div>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate('/pricing')}
            className="w-full bg-gradient-to-r from-purple-600 to-orange-500 text-white py-4 px-8 rounded-xl text-lg font-semibold hover:from-purple-700 hover:to-orange-600 transition-all duration-200 flex items-center justify-center group"
          >
            View Payment Plans
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="text-sm text-gray-500 mt-4">
            Pay in Full (Save £200) • Quarterly • Monthly options available
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccessGate;
